import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useDispatch } from 'react-redux'

import { filtrosArrayBarra } from '../../../../redux/actions'
import { ESimsService } from '../../../../services/http/eSims.service'
import { useGlobal } from '../../../../stores/useGlobal'
import {
  formatArrayToString,
  formatDateToDatePicker,
  formatDateToFilter,
  formatDateToGetFilter,
  formatStringToNumberArray
} from '../../../../utils/genericFormatters'
import DatePicker from '../../../Mantine/DatePicker'
import Grid from '../../../Mantine/Grid'
import Modal from '../../../Mantine/Modal'
import MultiSelect from '../../../Mantine/MultiSelect'

import * as S from './styles'

const REACT_QUERY_OPTIONS = {
  keepPreviousData: true,
  refetchOnWindowFocus: false
}

const WIDTH_COLUMNS = [6, 6, 6]

const DEFAULT_FILTERS_VALUES = {
  carriers: [],
  statuses: [],
  types: [],
  date: {
    start: null,
    end: null
  }
}

export default function FiltrosSolicitacaoEsim({ opened, close }) {
  const { filtros, setFiltros, setRevelarBarraFiltro, setIsLoadingGlobal } =
    useGlobal()
  const [filterOptions, setFilterOptions] = useState({
    carriers: [],
    statuses: [],
    types: []
  })
  const [localFilters, setLocalFilters] = useState({
    ...DEFAULT_FILTERS_VALUES
  })

  let arraySimcardFilters = []

  const despacho = useDispatch()

  useEffect(() => {
    if (!filtros.vazio) {
      const auxFilter = {
        ...DEFAULT_FILTERS_VALUES
      }

      if (filtros?.CARRIERS) {
        auxFilter.carriers = formatStringToNumberArray(filtros.CARRIERS)
      }

      if (filtros?.STATUS) {
        auxFilter.statuses = formatStringToNumberArray(filtros.STATUS)
      }

      if (filtros?.TYPE) {
        auxFilter.types = formatStringToNumberArray(filtros.TYPE)
      }

      if (filtros?.DATASTART && filtros?.DATAEND) {
        auxFilter.date = {
          start: formatDateToDatePicker(filtros?.DATASTART),
          end: formatDateToDatePicker(filtros?.DATAEND, true, true)
        }
      }

      setLocalFilters(auxFilter)
    }
  }, [filtros])

  const { getRequestsOptions } = ESimsService

  const getFilters = async () => {
    try {
      setIsLoadingGlobal(true)
      const filtersData = await getRequestsOptions()

      setFilterOptions((prev) => ({
        ...prev,
        carriers: filtersData?.carrier?.map((item) => ({
          value: item.id,
          label: item.label
        })),
        types: filtersData?.solicitationType?.map((item) => ({
          value: item.id,
          label: item.label
        })),
        statuses: filtersData?.solicitationStatus?.map((item) => ({
          value: item.id,
          label: item.label
        }))
      }))
    } catch (error) {
      console.error('Erro ao buscar filtros de estoque de eSIMs', error)
    } finally {
      setIsLoadingGlobal(false)
    }
  }

  useQuery(['esims/solicitation/filters'], async () => await getFilters(), {
    ...REACT_QUERY_OPTIONS
  })

  const handleLocalFiltroChange = (fieldPath, value) => {
    const [parent, child] = fieldPath.split('.')

    setLocalFilters((prev) => {
      if (!child) {
        return {
          ...prev,
          [parent]: value
        }
      }

      return {
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }
    })
  }

  const handleCleanFilters = () => {
    setLocalFilters({ ...DEFAULT_FILTERS_VALUES })
    setFiltros({ vazio: 'vazio' })
    despacho(filtrosArrayBarra([]))
    setRevelarBarraFiltro(false)
  }

  const formattedFilter = () => {
    const addFilters = (localFilterKey, filterOptionKey, prefix = '') => {
      const filteredItems = filterOptions[filterOptionKey].filter((item) =>
        localFilters[localFilterKey].includes(item.value)
      )

      const formattedItems = filteredItems.map(
        (item) => `${prefix}${item.label?.trim()} _${item.value}`
      )

      arraySimcardFilters.push(...formattedItems)
    }

    const addDateRangeFilter = (filterKey, label) => {
      const { start, end } = localFilters[filterKey] || {}
      if (start && end) {
        arraySimcardFilters.push(
          `${label}: ${formatDateToFilter(start)} e ${formatDateToFilter(end)}`
        )
      }
    }

    if (localFilters.carriers.length > 0)
      addFilters('carriers', 'carriers', 'carrierESim: ')
    if (localFilters.statuses.length > 0)
      addFilters('statuses', 'statuses', 'statusESim: ')
    if (localFilters.types.length > 0)
      addFilters('types', 'types', 'typeESim: ')

    addDateRangeFilter('date', 'dateESim')

    despacho(filtrosArrayBarra(arraySimcardFilters))
  }

  const formatarValor = (value, formatter) => {
    return value && value.length ? formatter(value) : undefined
  }

  const formatarData = (value) => {
    return value ? formatDateToGetFilter(value) : undefined
  }

  const handleFilter = () => {
    const filteredData = {
      CARRIERS: formatarValor(localFilters.carriers, formatArrayToString),
      TYPE: formatarValor(localFilters.types, formatArrayToString),
      STATUS: formatarValor(localFilters.statuses, formatArrayToString),
      DATASTART: formatarData(localFilters.date.start),
      DATAEND: formatarData(localFilters.date.end)
    }

    const finalFiltros = Object.fromEntries(
      Object.entries(filteredData).filter(([, value]) => value !== undefined)
    )

    setFiltros(finalFiltros)
    setRevelarBarraFiltro(true)

    formattedFilter()
    close()
  }

  return (
    <Modal
      width="1100px"
      type="filtro"
      titulo="Filtros"
      opened={opened}
      close={close}
      onConfirm={handleFilter}
      handleCleanFilters={handleCleanFilters}
    >
      <S.Container>
        <S.Card>
          <Grid responsive colSize={WIDTH_COLUMNS}>
            <MultiSelect
              label="Status"
              options={filterOptions.statuses}
              value={localFilters.statuses}
              onChange={(value) => handleLocalFiltroChange('statuses', value)}
            />

            <MultiSelect
              label="Tipo da solicitação"
              options={filterOptions.types}
              value={localFilters.types}
              onChange={(value) => handleLocalFiltroChange('types', value)}
            />

            <S.FieldsWrapper>
              <DatePicker
                label="Data da solicitação"
                prefixo="De:"
                value={localFilters.date.start}
                onChange={(date) => handleLocalFiltroChange('date.start', date)}
              />
              <DatePicker
                prefixo="Até:"
                minDate={localFilters.date.start}
                value={localFilters.date.end}
                onChange={(date) => handleLocalFiltroChange('date.end', date)}
              />
            </S.FieldsWrapper>
          </Grid>
        </S.Card>
      </S.Container>
    </Modal>
  )
}
